// Matematik paneli: hazır işaretler, formül şablonları, sayı doğrusu ve koordinat düzlemi.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { LuSigma } from 'react-icons/lu';
import { ANNOTATION_FONT } from '../../shared/geometry';
import { FORMULAS, MathInputError, SYMBOL_GROUPS, layoutCoordinatePlane, layoutFormula, layoutNumberLine, type Figure, type FormulaName } from '../../shared/math';
import { drawFigure } from '../canvas/draw';
import { lesson } from '../lib/lesson';
import { measureText } from '../lib/measure';
import { useStore } from '../lib/store';
import { SIZES, mathStore, setTool, toolStore } from '../lib/tools';

type DialogKind = FormulaName | 'numberLine' | 'coordinatePlane';

const FORMULA_ORDER: FormulaName[] = ['integral', 'sum', 'product', 'fraction', 'root', 'power', 'limit'];
const SIZE_OPTIONS = [
  { label: 'Küçük', value: 14 },
  { label: 'Orta', value: 20 },
  { label: 'Büyük', value: 28 },
];
const MAX_PREVIEW_W = 440;
const MAX_PREVIEW_H = 220;

const measure = measureText;
const lineWeight = () => SIZES.shape[toolStore.get().size.shape];
const toNumber = (s: string) => Number(s.trim().replace(',', '.').replace('−', '-'));

function FigurePreview({ figure }: { figure: Figure | null }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const color = useStore(toolStore, (s) => s.color.shape);
  useEffect(() => {
    const c = canvas.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    c.width = MAX_PREVIEW_W * dpr;
    c.height = MAX_PREVIEW_H * dpr;
    if (!figure) return;
    const k = Math.min(1, (MAX_PREVIEW_W - 16) / Math.max(1, figure.w), (MAX_PREVIEW_H - 16) / Math.max(1, figure.h));
    ctx.setTransform(k * dpr, 0, 0, k * dpr, 0, 0);
    drawFigure(ctx, {
      ...figure,
      color,
      x: (MAX_PREVIEW_W / k - figure.w) / 2,
      y: (MAX_PREVIEW_H / k - figure.h) / 2,
    });
  }, [figure, color]);
  return <canvas ref={canvas} className="math-preview" aria-hidden="true" />;
}

function FigureDialog({ kind, onClose }: { kind: DialogKind; onClose: () => void }) {
  const isFormula = kind !== 'numberLine' && kind !== 'coordinatePlane';
  const [values, setValues] = useState<Record<string, string>>(() => {
    if (isFormula) return Object.fromEntries(FORMULAS[kind].fields.map((f) => [f.key, f.initial]));
    if (kind === 'numberLine') return { min: '-5', max: '5', step: '1', length: '360' };
    return { xMin: '-5', xMax: '5', yMin: '-5', yMax: '5', cell: '24' };
  });
  const [size, setSize] = useState(20);
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    let alive = true;
    const done = () => alive && setFontsReady(true);
    if (document.fonts) document.fonts.load(`20px "${ANNOTATION_FONT}"`).then(done, done);
    else done();
    return () => {
      alive = false;
    };
  }, []);

  const result = useMemo<{ figure: Figure | null; error: string | null }>(() => {
    if (!fontsReady) return { figure: null, error: null };
    try {
      const n = (k: string) => toNumber(values[k] ?? '');
      let figure: Figure;
      if (isFormula) figure = layoutFormula(kind, values, size, lineWeight() * 0.7, measure);
      else if (kind === 'numberLine') figure = layoutNumberLine({ min: n('min'), max: n('max'), step: n('step'), length: Math.min(Math.max(n('length'), 80), 1000) }, size * 0.8, lineWeight() * 0.7, measure);
      else figure = layoutCoordinatePlane({ xMin: n('xMin'), xMax: n('xMax'), yMin: n('yMin'), yMax: n('yMax'), cell: Math.min(Math.max(n('cell'), 10), 60) }, size * 0.8, lineWeight() * 0.7, measure);
      return { figure, error: null };
    } catch (err) {
      return { figure: null, error: err instanceof MathInputError ? err.message : 'Bu değerlerle çizilemedi.' };
    }
  }, [fontsReady, kind, isFormula, values, size]);

  const fields: { key: string; label: string; numeric?: boolean }[] = isFormula
    ? FORMULAS[kind].fields
    : kind === 'numberLine'
      ? [
          { key: 'min', label: 'En küçük', numeric: true },
          { key: 'max', label: 'En büyük', numeric: true },
          { key: 'step', label: 'Adım', numeric: true },
          { key: 'length', label: 'Uzunluk (punto)', numeric: true },
        ]
      : [
          { key: 'xMin', label: 'x en küçük', numeric: true },
          { key: 'xMax', label: 'x en büyük', numeric: true },
          { key: 'yMin', label: 'y en küçük', numeric: true },
          { key: 'yMax', label: 'y en büyük', numeric: true },
          { key: 'cell', label: 'Kare boyutu (punto)', numeric: true },
        ];
  const title = isFormula ? FORMULAS[kind].label : kind === 'numberLine' ? 'Sayı doğrusu' : 'Koordinat düzlemi';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!result.figure) return;
    mathStore.set({ figure: result.figure, pendingSymbol: null });
    setTool('figure');
    onClose();
    lesson.toast('Yerleştirmek için sayfaya dokunun.');
  };

  return (
    <div className="math-backdrop" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="card math-dialog" role="dialog" aria-label={title} onSubmit={submit} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
        <h2>{title}</h2>
        <FigurePreview figure={result.figure} />
        <div className={`math-fields ${fields.length > 3 ? 'is-compact' : ''}`}>
          {fields.map((f, i) => (
            <label className="field" key={f.key}>
              <span>{f.label}</span>
              <input
                data-field={f.key}
                autoFocus={i === 0}
                inputMode={f.numeric ? 'decimal' : undefined}
                value={values[f.key] ?? ''}
                maxLength={f.numeric ? 8 : 60}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              />
            </label>
          ))}
        </div>
        <div className="math-sizes" role="radiogroup" aria-label="Boyut">
          {SIZE_OPTIONS.map((o) => (
            <button key={o.value} type="button" role="radio" aria-checked={size === o.value} className={`btn btn-sm ${size === o.value ? 'btn-on' : ''}`} onClick={() => setSize(o.value)}>
              {o.label}
            </button>
          ))}
        </div>
        {result.error && <p className="form-error">{result.error}</p>}
        <div className="math-actions">
          <button type="button" className="btn" onClick={onClose}>
            Vazgeç
          </button>
          <button type="submit" className="btn btn-primary" disabled={!result.figure}>
            Sayfaya yerleştir
          </button>
        </div>
      </form>
    </div>
  );
}

export function MathMenu({ disabled }: { disabled: boolean }) {
  const tool = useStore(toolStore, (s) => s.tool);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  const open = () => {
    // Panel ekran boyunda açılır ve kendi içinde kayar; böylece tüm işaretlere ulaşılır.
    setPos({ left: button.current!.getBoundingClientRect().right + 8, top: 8 });
  };

  useEffect(() => {
    if (!pos) return;
    const close = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setPos(null);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPos(null);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', key);
    };
  }, [pos]);

  useEffect(() => {
    if (disabled) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.toLowerCase() !== 'm') return;
      if (pos) setPos(null);
      else open();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, pos]);

  const pickSymbol = (symbol: string) => {
    const insert = mathStore.get().insert;
    if (insert) return insert(symbol); // panel açık kalır: art arda işaret eklenebilir
    mathStore.set({ pendingSymbol: symbol, figure: null });
    setTool('text');
    setPos(null);
    lesson.toast(`${symbol} için sayfada yazmak istediğiniz yere dokunun.`);
  };

  const openDialog = (kind: DialogKind) => {
    setPos(null);
    setDialog(kind);
  };

  return (
    <div ref={root}>
      <button
        ref={button}
        type="button"
        className={`icon-btn ${tool === 'figure' ? 'is-active' : ''}`}
        title="Matematik araçları (M)"
        aria-label="Matematik araçları (M)"
        aria-haspopup="dialog"
        aria-expanded={pos !== null}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()} // açık metin kutusu odağını kaybetmesin
        onClick={() => (pos ? setPos(null) : open())}
      >
        <LuSigma />
      </button>
      {pos && (
        <div className="math-panel" style={pos} onMouseDown={(e) => e.preventDefault()}>
          <h3>Formül</h3>
          <div className="math-templates">
            {FORMULA_ORDER.map((f) => (
              <button key={f} type="button" className="btn btn-sm" onClick={() => openDialog(f)}>
                {FORMULAS[f].label}
              </button>
            ))}
          </div>
          <h3>Çizim</h3>
          <div className="math-templates">
            <button type="button" className="btn btn-sm" onClick={() => openDialog('numberLine')}>
              Sayı doğrusu
            </button>
            <button type="button" className="btn btn-sm" onClick={() => openDialog('coordinatePlane')}>
              Koordinat düzlemi
            </button>
          </div>
          {SYMBOL_GROUPS.map((g) => (
            <div key={g.label}>
              <h3>{g.label}</h3>
              <div className="math-symbols">
                {g.symbols.map((s) => (
                  <button key={s} type="button" aria-label={`İşaret ${s}`} title={s} onClick={() => pickSymbol(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {dialog && <FigureDialog kind={dialog} onClose={() => setDialog(null)} />}
    </div>
  );
}
